import { useCallback } from "react";
import { motion } from "framer-motion";
import { PRODUCTS } from "../data/siteContent";
import { ChapterHeading, ScrollReveal, EASE } from "./shared";

const ProductCard = ({ product, index }) => {
    const onMove = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty("--spot-x", `${e.clientX - rect.left}px`);
        e.currentTarget.style.setProperty("--spot-y", `${e.clientY - rect.top}px`);
    }, []);

    return (
        <motion.article
            data-testid={`product-card-${index}`}
            initial={{ opacity: 0, y: 56 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.85, delay: (index % 3) * 0.12, ease: EASE }}
            onMouseMove={onMove}
            className={`spotlight-card group relative overflow-hidden border border-[#D9D9D9] bg-[#FAFAFA] col-span-1 ${product.span}`}
        >
            <div className="relative aspect-[16/10] overflow-hidden">
                <img
                    src={product.image}
                    alt={product.alt || product.name}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale-[35%] contrast-110 transition-[transform,filter] duration-700 ease-out group-hover:scale-[1.06] group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FAFAFA]/85 via-[#FAFAFA]/10 to-transparent" />
                <span className="absolute top-4 left-4 font-heading text-xs tracking-[0.25em] text-copper bg-[#FAFAFA]/70 backdrop-blur-sm px-3 py-1.5 border border-[#D9D9D9]">
                    {String(index + 1).padStart(2, "0")}
                </span>
            </div>
            <div className="relative z-[3] px-5 py-5 border-t border-[#D9D9D9] bg-[#FAFAFA]/85 backdrop-blur-sm">
                <div className="flex items-start justify-between gap-4">
                    <h3 className="font-heading uppercase font-medium text-xl md:text-2xl tracking-tight group-hover:text-copper transition-colors duration-300">
                        {product.name}
                    </h3>
                    <span className="font-heading text-2xl md:text-3xl text-outline shrink-0 leading-none pt-0.5">
                        {String(index + 1).padStart(2, "0")}
                    </span>
                </div>
                <p className="text-xs md:text-sm text-copper font-medium mt-1 uppercase tracking-[0.12em]">
                    {product.tag}
                </p>
                <p className="text-sm text-[#595959] mt-3 leading-relaxed">{product.description}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                    {product.specs.map((spec) => (
                        <span
                            key={spec}
                            data-testid={`product-${index}-spec-${spec.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="text-[10px] md:text-[11px] uppercase tracking-[0.14em] text-[#111111] border border-[#D9D9D9] bg-[#F0F0F0] px-2.5 py-1"
                        >
                            {spec}
                        </span>
                    ))}
                </div>
            </div>
        </motion.article>
    );
};

const Products = () => (
    <section
        id="products"
        data-testid="products-section"
        className="py-24 md:py-36 px-6 md:px-12 bg-[#F4F4F4] border-y border-[#E5E5E5]"
    >
        <ChapterHeading index="02" title="What We Manufacture" />

        <div className="mt-12 md:mt-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <ScrollReveal>
                <h2 className="font-heading uppercase font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight max-w-2xl">
                    Machined parts,
                    <br />
                    <span className="text-outline-copper">made to drawing.</span>
                </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.15}>
                <p className="text-base md:text-lg text-[#595959] max-w-sm leading-relaxed">
                    Six product lines, one obsession with fit and finish. Parallel keys machined to
                    DIN 6885 in EN8 and mild steel — standard sizes ready to ship, custom development
                    on request.
                </p>
            </ScrollReveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
            {PRODUCTS.map((p, i) => (
                <ProductCard key={p.name} product={p} index={i} />
            ))}
        </div>
    </section>
);

export default Products;
